<script lang="ts" setup>

import { reactive, computed } from "vue";

import * as relatoriosService from "@/services/relatoriosService";
import { fdata, fdataHora, SimNao } from "@/utils/funcoes";
import { useOrdenacao } from "@/composables/useOrdenacao";

import BreadCrumb from "@/components/Common/BreadCrumb.vue";
import Notificacao from "@/components/Notificacao/Index.vue";
import TrataTecla from "@/components/TrataTecla/Index.vue";
import DropdownVendedores from "@/components/Dropdowns/DropdownVendedores.vue";
import ReportView from "@/components/ReportView/Index.vue";
import ReportCabecalho from "@/components/ReportView/Cabecalho.vue";

const colunasOpcionais = [
  { chave: "nome", label: "Nome" },
  { chave: "cpf", label: "CPF" },
  { chave: "nascimento", label: "Nasc." },
  { chave: "idade", label: "Idade" },
  { chave: "cidade", label: "Cidade" },
  { chave: "dataCadastro", label: "Dt Cadastro" },
  { chave: "plano", label: "Plano" },
  { chave: "vendedor", label: "Vendedor" },
  { chave: "dataFim", label: "Dt Final" },
  { chave: "assinatura", label: "Assinatura" },
];

function calcIdade(dataNascimento: string | null): string {
  if (!dataNascimento) return "-";
  const nascimento = new Date(dataNascimento);
  const hoje = new Date();
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const aindaNaoFezAniversario =
    hoje.getMonth() < nascimento.getMonth() ||
    (hoje.getMonth() === nascimento.getMonth() && hoje.getDate() < nascimento.getDate());
  if (aindaNaoFezAniversario) idade--;
  return String(idade);
}

const controle = reactive({
  filtros: {
    comContrato: null,
    assinado: null,
    vendedorId: null,
    ativo: null,
    cortesia: null,
    dataCadastroInicial: null,
    dataCadastroFinal: null,
  } as relatoriosService.FiltroClientesContrato,

  itens: [] as relatoriosService.RelClienteContratoDTO[],
  totalRegistros: 0,

  colunasVisiveis: {
    nome: true,
    cpf: true,
    nascimento: true,
    idade: false,
    cidade: false,
    dataCadastro: true,
    plano: true,
    vendedor: true,
    dataFim: false,
    assinatura: true,
  } as Record<string, boolean>,

  erro: "",
  status: "",
  pesquisando: false,
});

async function pesquisar() {
  controle.pesquisando = true;
  controle.erro = "";
  try {
    const r = await relatoriosService.clientesContrato(controle.filtros);
    controle.totalRegistros = r.totalRegistros;
    controle.itens = r.dados;
  } catch (e: any) {
    controle.erro = e?.message || "Erro ao pesquisar";
  } finally {
    controle.pesquisando = false;
  }
}

const sort = useOrdenacao();

const itensOrdenados = computed(() => sort.aplicar(controle.itens, {
  id: (item: relatoriosService.RelClienteContratoDTO) => item.clienteId,
  idade: (item: relatoriosService.RelClienteContratoDTO) => Number(calcIdade(item.dataNascimento)) || -1,
  cidade: (item: relatoriosService.RelClienteContratoDTO) => item.cidadeNome || "",
  contrato: (item: relatoriosService.RelClienteContratoDTO) => item.contratoId ?? -1,
  plano: (item: relatoriosService.RelClienteContratoDTO) => item.planoNome || "",
  vendedor: (item: relatoriosService.RelClienteContratoDTO) => item.vendedorNome || "",
  dataFim: (item: relatoriosService.RelClienteContratoDTO) => item.dataFim || "",
  assinatura: (item: relatoriosService.RelClienteContratoDTO) => item.dataAssinatura || "",
}));

</script>

<template>
  <BreadCrumb PageTitle="Clientes X Contrato" :items="[
    { label: 'Relatórios', to: '/relatorios' },
    { label: 'Clientes X Contrato', to: '/relatorios/clientescontrato' }]" />

  <Notificacao :erro="controle.erro" @limparErro="controle.erro = ''" :progresso="controle.status"
    @limparProgresso="controle.status = ''" />

  <TrataTecla>
    <div class="card mb-2 d-print-none">
      <div class="card-body">
        <div class="row">

          <div class="col-md-2 col-6">
            <label>Com Contrato</label>
            <select v-model="controle.filtros.comContrato" class="form-select form-select-sm" id="comContrato">
              <option :value="null">Todos</option>
              <option :value="true">Sim</option>
              <option :value="false">Não</option>
            </select>
          </div>

          <div class="col-md-2 col-6">
            <label>Assinado</label>
            <select v-model="controle.filtros.assinado" class="form-select form-select-sm" id="assinado">
              <option :value="null">Todos</option>
              <option :value="true">Sim</option>
              <option :value="false">Não</option>
            </select>
          </div>

          <div class="col-md-2 col-6">
            <label>Vendedor</label>
            <DropdownVendedores v-model="controle.filtros.vendedorId" id="vendedorId" />
          </div>

          <div class="col-md-2 col-6">
            <label>Dt Cad Ini</label>
            <input v-model="controle.filtros.dataCadastroInicial" type="date" class="form-control form-control-sm" id="dataCadastroInicial" />
          </div>

          <div class="col-md-2 col-6">
            <label>Dt Cad Fin</label>
            <input v-model="controle.filtros.dataCadastroFinal" type="date" class="form-control form-control-sm" id="dataCadastroFinal" />
          </div>

          <div class="col-md-2 col-6">
            <label>Ativo</label>
            <select v-model="controle.filtros.ativo" class="form-select form-select-sm" id="ativo">
              <option :value="null">Todos</option>
              <option :value="true">Ativos</option>
              <option :value="false">Inativos</option>
            </select>
          </div>

          <div class="col-md-2 col-6">
            <label>Cortesia</label>
            <select v-model="controle.filtros.cortesia" class="form-select form-select-sm" id="cortesia">
              <option :value="null">Todos</option>
              <option :value="true">Com Cortesia</option>
              <option :value="false">Sem Cortesia</option>
            </select>
          </div>

          <div class="col-md-2 col-6">
            <a class="btn btn-primary btn-sm mt-4" @click="pesquisar">
              <i class="fa fa-search me-1"></i> Pesquisar
            </a>
          </div>

          <div class="col-md-2 col-6">
            <div class="dropdown">
              <a class="btn btn-outline-secondary btn-sm dropdown-toggle mt-4" href="#" role="button"
                data-bs-toggle="dropdown" aria-expanded="false">
                <i class="fa fa-table-columns me-1"></i> Colunas
              </a>
              <ul class="dropdown-menu p-2" style="min-width: 200px;">
                <li v-for="col in colunasOpcionais" :key="col.chave">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" :id="'col_' + col.chave"
                      v-model="controle.colunasVisiveis[col.chave]">
                    <label class="form-check-label" :for="'col_' + col.chave">{{ col.label }}</label>
                  </div>
                </li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="col-md-12">

      <ReportView :titulo="'Rel Clientes X Contrato'" :btnImprimir="true" :orientacao="'Paisagem'">

        <div v-if="controle.erro != ''" class="col-md-12 text-center">
          <div class="p-4 text-danger fw-14">{{ controle.erro }}</div>
        </div>

        <div v-if="controle.erro == '' && controle.pesquisando" class="col-md-12 text-center">
          <div class="p-4">
            <i class="fa fa-cog fa-2x fa-spin"></i>
            <div class="fw-16">Pesquisando...</div>
          </div>
        </div>

        <template v-if="controle.erro == '' && !controle.pesquisando && controle.itens.length > 0">

          <ReportCabecalho :Titulo="'Relatório de Clientes X Contrato'" :Filtros="`Com Contrato: ${controle.filtros.comContrato === true ? 'Sim' : controle.filtros.comContrato === false ? 'Não' : 'Todos'}  |  Assinado: ${controle.filtros.assinado === true ? 'Sim' : controle.filtros.assinado === false ? 'Não' : 'Todos'}  |  Ativo: ${controle.filtros.ativo === true ? 'Ativos' : controle.filtros.ativo === false ? 'Inativos' : 'Todos'}  |  Cortesia: ${controle.filtros.cortesia === true ? 'Com Cortesia' : controle.filtros.cortesia === false ? 'Sem Cortesia' : 'Todos'}  |  Dt Cadastro: ${controle.filtros.dataCadastroInicial ? fdata(controle.filtros.dataCadastroInicial) : '...'} a ${controle.filtros.dataCadastroFinal ? fdata(controle.filtros.dataCadastroFinal) : '...'}`" />

          <div class="mb-1 text-muted f12px">Total: {{ controle.itens.length }} registro(s)</div>

          <div style="overflow-x: auto;">
          <table class="tabela table-sm table-bordered f12px" id="tabela-relatorio">
            <thead>
              <tr class="table-relatorio">
                <th class="text-center colId text-nowrap col-ordenavel" @click="sort.alternar('id')">Id <i :class="sort.classeIcone('id')"></i></th>
                <th v-if="controle.colunasVisiveis.nome" class="text-start colNome col-ordenavel" @click="sort.alternar('nome')">Nome <i :class="sort.classeIcone('nome')"></i></th>
                <th v-if="controle.colunasVisiveis.cpf" class="text-center colCpf text-nowrap col-ordenavel" @click="sort.alternar('cpf')">CPF <i :class="sort.classeIcone('cpf')"></i></th>
                <th v-if="controle.colunasVisiveis.nascimento" class="text-center colNasc text-nowrap col-ordenavel" @click="sort.alternar('dataNascimento')">Nasc. <i :class="sort.classeIcone('dataNascimento')"></i></th>
                <th v-if="controle.colunasVisiveis.idade" class="text-center colIdade text-nowrap col-ordenavel" @click="sort.alternar('idade')">Idade <i :class="sort.classeIcone('idade')"></i></th>
                <th v-if="controle.colunasVisiveis.cidade" class="text-start colCidade text-truncate col-ordenavel" @click="sort.alternar('cidade')">Cidade <i :class="sort.classeIcone('cidade')"></i></th>
                <th v-if="controle.colunasVisiveis.dataCadastro" class="text-center colData text-nowrap col-ordenavel" @click="sort.alternar('dataCadastro')">Dt Cadastro <i :class="sort.classeIcone('dataCadastro')"></i></th>
                <th class="text-center colContrato text-nowrap col-ordenavel" @click="sort.alternar('contrato')">Contrato <i :class="sort.classeIcone('contrato')"></i></th>
                <th v-if="controle.colunasVisiveis.plano" class="text-start colPlano text-truncate col-ordenavel" @click="sort.alternar('plano')">Plano <i :class="sort.classeIcone('plano')"></i></th>
                <th v-if="controle.colunasVisiveis.vendedor" class="text-start colVendedor text-truncate col-ordenavel" @click="sort.alternar('vendedor')">Vendedor <i :class="sort.classeIcone('vendedor')"></i></th>
                <th v-if="controle.colunasVisiveis.dataFim" class="text-center colDataFim text-nowrap col-ordenavel" @click="sort.alternar('dataFim')">Dt Final <i :class="sort.classeIcone('dataFim')"></i></th>
                <th v-if="controle.colunasVisiveis.assinatura" class="text-center colData text-nowrap col-ordenavel" @click="sort.alternar('assinatura')">Assinatura <i :class="sort.classeIcone('assinatura')"></i></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in itensOrdenados" :key="idx">
                <td class="text-center colId text-nowrap">{{ item.clienteId }}</td>
                <td v-if="controle.colunasVisiveis.nome" class="text-start colNome">{{ item.nome }}</td>
                <td v-if="controle.colunasVisiveis.cpf" class="text-center colCpf text-nowrap">{{ item.cpf }}</td>
                <td v-if="controle.colunasVisiveis.nascimento" class="text-center colNasc text-nowrap">{{ fdata(item.dataNascimento) }}</td>
                <td v-if="controle.colunasVisiveis.idade" class="text-center colIdade text-nowrap">{{ calcIdade(item.dataNascimento) }}</td>
                <td v-if="controle.colunasVisiveis.cidade" class="text-start colCidade text-truncate">{{ item.cidadeNome ?? '-' }}</td>
                <td v-if="controle.colunasVisiveis.dataCadastro" class="text-center colData text-nowrap">{{ fdata(item.dataCadastro) }}</td>
                <td class="text-center colContrato text-nowrap">{{ item.contratoId ?? '-' }}</td>
                <td v-if="controle.colunasVisiveis.plano" class="text-start colPlano text-truncate">{{ item.planoNome ?? '-' }}</td>
                <td v-if="controle.colunasVisiveis.vendedor" class="text-start colVendedor text-truncate">{{ item.vendedorNome ?? '-' }}</td>
                <td v-if="controle.colunasVisiveis.dataFim" class="text-center colDataFim text-nowrap">{{ fdata(item.dataFim) }}</td>
                <td v-if="controle.colunasVisiveis.assinatura" class="text-center colData text-nowrap">{{ fdata(item.dataAssinatura) }}</td>
              </tr>
            </tbody>
          </table>
          </div>

        </template>

        <template v-if="controle.erro == '' && !controle.pesquisando && controle.itens.length === 0 && controle.totalRegistros === 0 && controle.status === ''">
          <div class="text-center p-4 text-muted">
            Utilize os filtros acima e clique em <strong>Pesquisar</strong> para gerar o relatório.
          </div>
        </template>

        <template v-if="controle.erro == '' && !controle.pesquisando && controle.itens.length === 0 && controle.totalRegistros > 0">
          <div class="text-center p-4 text-muted">Nenhum registro encontrado com os filtros selecionados.</div>
        </template>

      </ReportView>

    </div>
  </TrataTecla>
</template>

<style lang="scss" scoped>
.colId {
  width: 50px;
}
.colContrato {
  width: 60px;
}
.colCpf {
  width: 110px;
}
.colNasc {
  width: 85px;
}
.colIdade {
  width: 60px;
}
.colCidade {
  width: 120px;
}
.colDataFim {
  width: 65px;
}
.colData {
  width: 85px;
}
.colPlano {
  width: 160px;
}
.colVendedor {
  width: 160px;
}
#tabela-relatorio {
  table-layout: fixed;
  min-width: 1200px;
  width: 100%;
}
#tabela-relatorio th,
#tabela-relatorio td {
  font-size: 12px !important;
  padding: 3px !important;
}

@media print {
  #tabela-relatorio {
    min-width: 0;
  }
}
</style>
